import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import React from "react";
import PropTypes from "prop-types";
import { useClasses } from "@xo-union/react-css-modules";
import { useUniqueID } from "@xo-union/react-unique-id";
import classesPropType from "@xo-union/classes-prop-type";
import { labelize } from "../../utilities/index.js";
import Input from "../Input/index.js";
import styles from "./styles.css";
import WithValidations from "../WithValidations.js";
import SubText from "../SubText.js";
const Field = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _context;

  const {
    name,
    subText,
    type,
    state,
    label = labelize(name),
    id: idProp,
    'aria-describedby': ariaDescribedByIds,
    classes: classesProp,
    ...restOfProps
  } = props;
  const id = useUniqueID(idProp);
  const classes = useClasses(styles, classesProp);
  const validationClassMap = {
    neutral: classes['is-neutral'],
    invalid: classes['is-invalid'],
    valid: classes['is-valid']
  };
  const validationClass = validationClassMap[state];
  let subtextId;

  if (subText) {
    subtextId = `${id}-subtext`;
  }

  const ariaDescribedBy = _filterInstanceProperty(_context = [ariaDescribedByIds, subtextId]).call(_context, Boolean).join(' ') || null;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement(Input, _extends({
    ref: ref,
    type: type,
    className: `${classes.input} ${validationClass}`,
    id: id,
    name: name,
    placeholder: " ",
    "aria-describedby": ariaDescribedBy
  }, restOfProps)), /*#__PURE__*/React.createElement("label", {
    className: classes.label,
    htmlFor: id
  }, label), subText && /*#__PURE__*/React.createElement(SubText, {
    state: state,
    className: classes['sub-text'],
    id: subtextId
  }, subText));
});
Field.defaultProps = {
  state: 'neutral',
  type: 'text'
};
const validationProp = process.env.NODE_ENV !== "production" ? PropTypes.any : {};
process.env.NODE_ENV !== "production" ? Field.propTypes = {
  /**
   * Name used for input element
   */
  name: PropTypes.string.isRequired,

  /**
   * The input's label string. If not provided, it is assumed from the name.
   */
  label: PropTypes.string,

  /**
   * Visual state. This has no effect when validations are enabled.
   */
  state: PropTypes.oneOf(['neutral', 'valid', 'invalid']),

  /**
   * Subtext message used to provide the user with extra information. The subText will appear just below the field.
   *
   *
   * When validations are enabled, this value is used for the `neutral` and the `valid` state (if `subTextOnValid` is not specified). The given validators will determine what subText will render on `invalid`, unless otherwise specified by the `subTextOnInvalid` prop.
   */
  subText: PropTypes.node,

  /**
   * The input's aria-describedby value. This will be appended to based on the subText value.
   */
  'aria-describedby': PropTypes.string,

  /**
   * The input type
   */
  type: PropTypes.string,

  /**
   * The input's id. If not provided, it is assumed from the name.
   */
  id: PropTypes.string,

  /**
   * Classes dependencies
   */
  classes: classesPropType(['container', 'input', 'is-valid', 'is-invalid', 'label', 'sub-text']),

  /**
   * Called when the `value` content attribute is added, set, or removed
   */
  onChange: PropTypes.func,

  /**
   * Current value of the `input` element. In the React rendering lifecycle, the `value` attribute on form elements will override the value in the DOM.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

  /**
   * Reflects the default value of the `input` field. Use this if you want React to specify the initial value but leave subsequent updates uncontrolled.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

  /**
   * Validations to apply. See [Input Component](/packages/@xo-union/ui-form-validations#input).
   */
  validations: validationProp,

  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `invalid`. When a function is provided, the function will receive the errors generated by the validators. The return value should be a React.Node type.
   */
  subTextOnInvalid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `valid`. This is only used when validations are enabled.
   */
  subTextOnValid: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
} : void 0;
export default WithValidations(Field);